// Which orders can be cancelled, by whom. Mirrors qerchabackend/utils/orderCancellation.js; the server decides,
// this only decides what to show.
export const CUSTOMER_CANCELLABLE = ['pending', 'paid'];
export const SUPPLIER_CANCELLABLE = ['pending', 'paid', 'processing', 'packed', 'ready_for_pickup', 'shipped', 'out_for_delivery'];
export const MIN_SUPPLIER_REASON = 10;

const usable = (o) => o.paymentStatus !== 'creating' && o.status !== 'cancelled';

// A cash order of a per-order checkout: confirmed and cancelled one order at a time (the server's cashStock marks it)
export const isPerOrderCash = (o) => o.paymentMethod === 'cash' && o.cashStock != null;

// A legacy cash group (one checkout and supplier, no per-order stock) is acted on as a whole. That is only safe while every
// order is unpaid; a group of several orders with a paid order needs manual reconciliation. Mirrors the server's guard.
export const legacyGroupNeedsReconcile = (group) => group.length > 1
  && group.some((o) => o.paymentMethod === 'cash' && o.cashStock == null)
  && !group.every((o) => o.paymentStatus === 'awaiting_cash');

export const canCustomerCancel = (o) => usable(o) && CUSTOMER_CANCELLABLE.includes(o.status);
export const canSupplierCancel = (o) => usable(o) && SUPPLIER_CANCELLABLE.includes(o.status);

// Why the customer cannot cancel an order (a translation key), or null when they can or it is already cancelled
export function customerBlockReason(o) {
  if (o.status === 'cancelled' || canCustomerCancel(o)) return null;
  if (SUPPLIER_CANCELLABLE.includes(o.status)) return 'cancel.preparing';
  return 'cancel.finished';
}

// Groups an entry's orders by supplier, so one supplier's orders cancel together
export function bySupplier(orders) {
  const groups = new Map();
  for (const o of orders) {
    const key = String(o.supplierId?._id || o.supplierId || '');
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(o);
  }
  return [...groups.values()];
}

// The refund status to show for a set of orders: the most urgent one
const REFUND_ORDER = ['manual_due', 'pending', 'failed', 'unavailable', 'succeeded', 'manual_recorded', 'none'];
export function refundSummary(orders) {
  const statuses = orders.map((o) => o.refund?.status || 'none');
  return REFUND_ORDER.find((s) => statuses.includes(s)) || 'none';
}

// The message for a cancelled supplier group, as a translation key, from the server's existing fields.
// Unpaid cash: no payment was collected. Paid cash: refund pending until it is settled by hand. Card: follows Stripe.
// Null when the group is not cancelled.
export function cancellationStatusKey(orders) {
  if (!orders.length || !orders.every((o) => o.status === 'cancelled')) return null;
  const refund = refundSummary(orders);
  if (orders.every((o) => o.paymentMethod === 'cash')) {
    if (refund === 'manual_recorded') return 'cancel.msgRefunded';
    if (refund === 'manual_due') return 'cancel.msgCashRefundPending';
    return 'cancel.msgUnpaidCash';
  }
  if (refund === 'succeeded' || refund === 'manual_recorded') return 'cancel.msgRefunded';
  if (refund === 'pending') return 'cancel.msgRefundProcessing';
  if (refund === 'failed') return 'refund.failed';
  if (refund === 'unavailable') return 'refund.unavailable';
  return 'cancel.msgCancelled';
}

// Who cancelled an order, when and why, from the order's cancellation record (no actorId: never shown).
// Returns null when there is no record. Lines are translated by the caller's t().
export function cancellationDetails(t, o) {
  const c = o?.cancellation;
  if (!c?.reason) return null;
  return {
    who: t(c.by === 'customer' ? 'cancel.byCustomer' : 'cancel.bySupplier'),
    when: c.at ? t('cancel.details', { at: new Date(c.at).toLocaleString() }) : null,
    reason: t('cancel.reasonShown', { reason: c.reason }),
  };
}
